import { createElement } from "react";
import React from "react";
import { ValueAndPage } from "./valueandpage";
import { makeStateObservableToReact } from "../../utilities";

export class StateMachine<TResultState, TState extends object> {
    private masterData: Map<ValueAndPage<TResultState>, Array<TState>>

    constructor(masterData: Map<ValueAndPage<TResultState>, Array<TState>>) {
        this.masterData = masterData
        this.validateTState();
    }

    /**
     * We want to enforce that only types with {@link ValueAndPage} fields are accepted by this generic class.
     * However, the TypeScript type system is not powerful enough to express this as a generic constraint.
     * So, let's throw it into a separate runtime function.
     */
    private validateTState() {
        if (this.masterData.size === 0) {
            throw new Error("The state machine needs master data which details the result states and how to get there.")
        }

        // So... TypeScript does not allow me to iterate over `TState`, I need some instance of it.
        // Let's grab the first instance out of the master data.
        // Also, this destructing thing is amazing...
        const [[, [example]]] = this.masterData

        if (Object.keys(example).length === 0) {
            throw new Error("The state type cannot be empty. It needs to have fields to represent the state.")
        }

        for (let index in Object.keys(example)) {
            if (!(Object.values(example)[index] instanceof ValueAndPage)) {
                throw new Error("The state machine can only be used with a state type that has fields of type `ValueAndPage`")
            }
        }
    }

    /**
     * Gets the result value of the state machine, if any.
     * 
     * Feel free to use the helper function {@link renderResult} to render the result page.
     * 
     * If the given state contains enough information to satisfied one of the rules in the master data of the state machine, 
     * the result value of that rule is returned.
     * If not enough information is available to come to a conclusion, `undefined` is returned. In this case, the caller is encouraged to call {@link renderNextStep}.
     * 
     * @param state State to test
     * @returns The result value of the state. Or undefined.
     */
    getResult(state: TState): ValueAndPage<TResultState> | undefined {
        for (let [key, rules] of this.masterData) {
            if (rules.some((rule) => this.itemSatisfiesRule(state, rule))) {
                return key
            }
        }
    }

    /**
     * Renders a result page for the given result.
     * 
     * Be sure to call {@link getResult} to check whether the state machine has ended before calling this function.
     * In case the state machine is not in a result state, this function will throw.
     * 
     * @param result The result to render the page for
     * @param goToIntroPage A callback that results the state of the application to the intro page
     * @returns A rendered page
     */
    renderResult(state: TState, goToIntroPage: () => void): React.JSX.Element {
        const result = this.getResult(state)

        if (result === undefined) {
            throw new Error("Programming error! This function may only be called if the state machine is in a result state.")
        }

        return createElement(result.pageFunction, {
            goToIntroPage: goToIntroPage
        })
    }

    /**
     * Returns the next step that must be completed in order to come closer to a result in the state machine.
     * 
     * Be sure to call {@link getResult} to check whether the state machine has ended before calling this function.
     * In case the state matches a result state, this function will throw.
     * In case the state is no partial match for any of the rules, then the state machine will never finish, and this function will throw.
     * 
     * If you just want to figure out what the next property is without rendering the corresponding page, see {@link getNextProperty}.
     */
    renderNextStep(state: TState, setState: (arg0: TState) => void): React.JSX.Element {
        const property = this.getNextProperty(state)

        const field = state[property] as ValueAndPage<unknown>
        type fieldType = typeof field.value

        return createElement(field.pageFunction, {
            setState: (input: fieldType) => {
                field.value = input
                setState(makeStateObservableToReact(state))
            }
        })
    }

    /**
     * Figures out the next property that must be answered in order to move towards a result state.
     * Normally, you won't need this function. Instead, you can directly render the page for the next step via {@link renderNextStep}.
     */
    getNextProperty(state: TState): keyof TState {
        for (let [, rules] of this.masterData) {
            for (let rule of rules) {
                if (this.isPartialMatch(state, rule)) {
                    return this.getNextPropertyToFullyMatchStateToRule(state, rule)
                }
            }
        }

        throw new Error("The current state is not a partial match for any of the rules. We will never be able to reach a result state!")
    }

    /**
     * Returns the next property of `state` that must be filled in to move towards a full match.
     * State must be a partial match for the rule.
     * This function may only be called if {@link isPartialMatch} returns true.
            */
    private getNextPropertyToFullyMatchStateToRule(state: TState, rule: TState): keyof TState {
        let property: keyof TState

        for (property in rule) {
            const ruleField = rule[property] as ValueAndPage<unknown>
            const stateField = state[property] as ValueAndPage<unknown>
            if (ruleField.value !== undefined && stateField.value === undefined) {
                return property
            }
        }

        throw new Error("Programming error: this function should only be called if state is a partial match!")
    }

    private isPartialMatch(state: TState, rule: TState): boolean {
        let property: keyof TState
        let result = false;

        for (property in rule) {
            const ruleField = rule[property] as ValueAndPage<unknown>
            const stateField = state[property] as ValueAndPage<unknown>

            // If we have a different value for a field, we certainly don't have a partial match.
            if (ruleField.value !== undefined && stateField.value !== undefined) {
                if (ruleField.value !== stateField.value) {
                    return false
                }
            }

            // If the rule needs a value for a field and that field is still empty in the state, the state could be a partial match.
            if (ruleField.value !== undefined && stateField.value === undefined) {
                result = true
            }
        }

        return result
    }

    /**
      * Returns whether the item satisfies the given rule.
      * 
      * Satisfies means that all fields that have a value in the rule have the same value in this object.
      * If a field has no value in the rule, it is ignored. 
      */
    private itemSatisfiesRule(item: TState, rule: TState): boolean {
        let property: keyof TState

        // We loop over all properties of the rule.
        for (property in rule) {
            // We do a quick type check to be sure that we are looping over the types we think we are looping over.
            const conceptField = rule[property] as ValueAndPage<unknown>
            const itemField = item[property] as ValueAndPage<unknown>
            // If a property has a value in the rule and that value is not identical to the value in the item,
            // then the item does not satisfy the rule.
            if (conceptField.value !== undefined && conceptField.value !== itemField.value) {
                return false
            }
        }

        // If we've looped over all properties in the rule without finding an issue, the item satisfies the rule.
        return true
    }
}